import { Component, OnInit, Input, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Column, ColumnItem, DragData, fileEditConfig, FileService, Folder, PreviewColumn, SortOption, UploadedFile } from '@app/services/file.service';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatTree } from '@angular/material/tree';
import { ConfirmDialogComponent } from '@app/components/_common/confirm-dialog/confirm-dialog.component';
import { DocumentEditComponent } from './edit/edit.component';
import { concatMap, filter, takeUntil, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { PlatformService, SnackbarService } from '@app/services';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { from, forkJoin } from 'rxjs';

export interface FileTreeNode {
  name: string;
  type: 'folder' | 'file';
  folder?: Folder;
  file?: UploadedFile;
  children?: FileTreeNode[];
}

@Component({
  selector: 'admin-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.scss'],
  standalone: false
})
export class FileManagerComponent implements OnInit {

  @Input() readOnly: string | boolean = false; // pass 'add' to allow upload, but disable delete
  @Input() selectMode: string | boolean; // false / true or 'multi' (single by default)
  @Input() type: string;
  @Input() documentPicker = false;

  selection: SelectionModel<UploadedFile>;
  fileSelection = new SelectionModel<UploadedFile>(true, []);
  multiSelectColumnIndex: number | null = null;
  loading = false;

  @ViewChild('container') containerRef!: ElementRef;
  @ViewChild('inputDialog') inputDialogTemplate!: any;

  columns: Column[] = [];
  previewColumn: PreviewColumn | null = null;
  selectedFolderIds: (number | null)[] = [];
  selectedFileId: number | null = 0;
  selectedColumnIndex: number | null = null;
  selectedType: 'file' | 'folder' | null = null;
  columnWidths: number[] = [];
  resizingColumnIndex: number | null = null;

  // Drag & Drop
  dragData: DragData | null = null;
  isDragOver: number | null = null;

  // Loading state pour les items en cours de modification
  loadingItems = new Set<string>();

  // Recherche
  searchQuery: string = '';
  searchResults: { files: UploadedFile[], folders: Folder[] } | null = null;
  private allFolders: Folder[] = [];
  private allFiles: UploadedFile[] = [];

  // Dialog
  dialogTitle: string = '';
  dialogLabel: string = '';
  dialogValue: string = '';
  dialogConfirmText: string = '';
  private dialogCallback: ((value: string) => void) | null = null;

  folders: Folder[];
  files: UploadedFile[];

  // View mode
  viewMode: 'columns' | 'tree' | 'single' = 'columns';

  // Single column view
  singleColumn: Column | null = null;
  singleColumnFolderHistory: number[] = [];
  treeData: FileTreeNode[] = [];
  treeChildrenAccessor = (node: FileTreeNode): FileTreeNode[] => node.children ?? [];
  treeHasChild = (_: number, node: FileTreeNode): boolean => !!node.children && node.children.length > 0;
  treeSelectedFolderId: number | null = null;
  treeMultiSelect = false;
  expandedFolderIds = new Set<number>();
  @ViewChild('fileTree') fileTree: MatTree<FileTreeNode>;

  constructor(
    private fileService: FileService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    public platformService: PlatformService,
  ) { }

  ngOnInit() {
    // Initialiser le SelectionModel si selectMode est activé
    if (this.selectMode) {
      const isMulti = this.selectMode === 'multi';
      this.selection = new SelectionModel<UploadedFile>(isMulti, []);
    }

    this.loading = true;
    forkJoin({
      files: this.fileService.getAll(),
      folders: this.fileService.getFolders()
    }).subscribe(({ files, folders }) => {
      this.folders = folders;
      this.files = files;
      this.allFolders = [...folders];
      this.allFiles = [...files];
      this.initializeColumns();
      this.singleColumnFolderHistory = [];
      this.initializeSingleColumn();
      this.buildTreeData();
      this.loading = false;
    });
  }

  initializeColumns() {
    this.columns = [{
      folders: this.getFoldersByParent(0),
      files: this.getFilesByFolder(0),
      items: [],
      currentFolderId: 0,
      sortBy: 'name',
      sortOrder: 'asc'
    }];
    this.columnWidths = [280];
    this.applySortToColumn(0);
  }

  getFoldersByParent(parentId: number | null): Folder[] {
    return this.folders.filter(f => f.idRepertoireParent === parentId);
  }

  getFilesByFolder(folderId: number | null): UploadedFile[] {
    if (folderId === 0) {
      return this.files.filter(f => f.idRepertoire === 0 || !this.folders.some(folder => folder.idRepertoire === f.idRepertoire));
    }
    return this.files.filter(f => f.idRepertoire === folderId);
  }

  getFolderName(folderId: number): string {
    const folder = this.folders.find(f => f.idRepertoire === folderId);
    return folder ? folder.nom : 'Racine';
  }

  handleFolderClick(folder: Folder, columnIndex: number) {
    // Si on clique sur le dossier déjà sélectionné, le déselectionner et revenir à la colonne précédente
    if (this.selectedFolderIds[columnIndex] === folder.idRepertoire && this.selectedType === 'folder') {
      this.selectedFolderIds = this.selectedFolderIds.slice(0, columnIndex);
      this.selectedType = columnIndex > 0 ? 'folder' : null;
      this.previewColumn = null;

      // Garder uniquement les colonnes jusqu'à celle-ci (incluse)
      this.columns = this.columns.slice(0, columnIndex + 1);
      this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);

      return;
    }

    this.selectedFolderIds = [...this.selectedFolderIds.slice(0, columnIndex), folder.idRepertoire];
    this.selectedFileId = 0;
    this.selectedColumnIndex = columnIndex;
    this.selectedType = 'folder';
    this.previewColumn = null;

    // En mode sélection, désélectionner tous les fichiers
    if (this.selectMode && this.selection) {
      this.selection.clear();
    }

    this.columns = this.columns.slice(0, columnIndex + 1);
    this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);

    this.columns.push({
      folders: this.getFoldersByParent(folder.idRepertoire),
      files: this.getFilesByFolder(folder.idRepertoire),
      items: [],
      currentFolderId: folder.idRepertoire,
      sortBy: 'name',
      sortOrder: 'asc'
    });
    this.columnWidths.push(280);
    this.applySortToColumn(columnIndex + 1);

  }

  handleFileClick(file: UploadedFile, columnIndex: number) {
    const isAlreadySelected = this.selectedFileId === file.id && this.selectedType === 'file' && this.selectedColumnIndex === columnIndex;

    // Si on clique sur le fichier déjà sélectionné, le désélectionner et fermer la preview
    if (isAlreadySelected) {
      this.selectedFileId = 0;
      // Restaurer le type de sélection vers le dossier parent s'il y en a un
      this.selectedType = this.selectedFolderIds.length > 0 ? 'folder' : null;
      this.selectedColumnIndex = this.selectedFolderIds.length > 0 ? this.selectedFolderIds.length - 1 : null;
      this.previewColumn = null;

      // En mode sélection, désélectionner aussi le fichier
      if (this.selectMode && this.selection?.isSelected(file)) {
        this.selection.deselect(file);
      }
      return;
    }

    // En mode sélection, gérer la sélection du fichier
    if (this.selectMode && this.selection) {
      // Désélectionner l'ancien fichier s'il y en a un (mode single)
      if (this.selectMode !== 'multi') {
        this.selection.clear();
      }
      this.selection.select(file);
    }

    this.selectedFileId = file.id;
    this.selectedColumnIndex = columnIndex;
    this.selectedType = 'file';
    this.selectedFolderIds = this.selectedFolderIds.slice(0, columnIndex);

    this.columns = this.columns.slice(0, columnIndex + 1);
    this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);

    this.previewColumn = {
      file: file,
      currentFolderId: this.columns[columnIndex].currentFolderId || 0,
      fileLoaded: false
    };

  }

  onContextMenu(event: MouseEvent, type: 'file' | 'folder', item: any, columnIndex: number) {
    event.preventDefault();
  }

  // Sélection de fichiers (mode document picker)
  toggleFileSelection(event: Event, file: UploadedFile) {
    event.stopPropagation();
    if (this.selection) {
      this.selection.toggle(file);
    }
  }

  toggleFileWithPreview(event: Event, file: UploadedFile, columnIndex: number) {
    event.stopPropagation();
    // Si le fichier est déjà sélectionné, on le désélectionne et on ferme la preview
    if (this.selection?.isSelected(file)) {
      this.selection.deselect(file);
      this.selectedFileId = 0;
      // Restaurer le type de sélection vers le dossier parent s'il y en a un
      this.selectedType = this.selectedFolderIds.length > 0 ? 'folder' : null;
      this.selectedColumnIndex = this.selectedFolderIds.length > 0 ? this.selectedFolderIds.length - 1 : null;
      this.previewColumn = null;
    } else {
      // Sinon on sélectionne et on ouvre la preview
      if (this.selection) {
        if (this.selectMode !== 'multi') {
          this.selection.clear();
        }
        this.selection.select(file);
      }
      this.selectedFileId = file.id;
      this.selectedColumnIndex = columnIndex;
      this.selectedType = 'file';
      this.selectedFolderIds = this.selectedFolderIds.slice(0, columnIndex);
      this.columns = this.columns.slice(0, columnIndex + 1);
      this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);
      this.previewColumn = {
        file: file,
        currentFolderId: this.columns[columnIndex].currentFolderId || 0,
        fileLoaded: false
      };
    }
  }

  isFileSelected(file: UploadedFile): boolean {
    return this.selection?.isSelected(file) ?? false;
  }

  // Actions sur les dossiers
  createFolder(columnIndex: number) {
    this.openDialog(
      'Nouveau dossier',
      'Nom du dossier',
      'Nouveau dossier',
      'Créer',
      (name: string) => {
        const tempId = -Date.now();
        const tempFolder: Folder = {
          idRepertoire: tempId,
          idRepertoireParent: this.columns[columnIndex].currentFolderId,
          nom: name
        };
        this.folders.push(tempFolder);
        this.refreshColumns();
        this.setFolderLoading(tempId);

        this.fileService.createFolder({ idRepertoire: null, idRepertoireParent: tempFolder.idRepertoireParent, nom: name }).subscribe({
          next: (folder: Folder) => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1, folder);
            this.allFolders.push(folder);
            this.refreshColumns();
            this.snackbarService.info(`Dossier "${name}" créé`, 2000);
          },
          error: (e) => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1);
            this.refreshColumns();
            this.snackbarService.error(`Erreur lors de la création du dossier "${name}"`);
          }
        })
      }
    );
  }

  renameFolder(folder: Folder) {
    this.openDialog(
      'Renommer le dossier',
      'Nouveau nom',
      folder.nom,
      'Renommer',
      (newName: string) => {
        const folderIndex = this.folders.findIndex(f => f.idRepertoire === folder.idRepertoire);
        if (folderIndex !== -1) {
          const oldName = this.folders[folderIndex].nom;
          this.setFolderLoading(folder.idRepertoire);
          this.fileService.updateFolder(folder.idRepertoire, { nom: newName, idRepertoireParent: folder.idRepertoireParent }).subscribe({
            next: (_) => {
              this.clearFolderLoading(folder.idRepertoire);
              this.folders[folderIndex].nom = newName;
              this.allFolders[this.allFolders.findIndex(f => f.idRepertoire === folder.idRepertoire)].nom = newName;
              this.refreshColumns();
              this.snackbarService.info(`Dossier "${oldName}" renommé en "${newName}"`, 2000);
            },
            error: (e) => {
              this.clearFolderLoading(folder.idRepertoire);
              this.snackbarService.error(`Erreur lors du renommage du dossier "${oldName}"`);
            }
          })
        }
      }
    );
  }

  deleteFolder(folder: Folder) {
    // Vérifier si le dossier contient des fichiers
    const hasFiles = this.files.some(f => f.idRepertoire === folder.idRepertoire);
    const hasSubfolders = this.folders.some(f => f.idRepertoireParent === folder.idRepertoire);

    if (hasFiles || hasSubfolders) {
      this.snackbarService.info('Impossible de supprimer un dossier non vide', 3000);
      return;
    }

    const folderIndex = this.folders.findIndex(f => f.idRepertoire === folder.idRepertoire);
    if (folderIndex !== -1) {
      this.setFolderLoading(folder.idRepertoire);
      this.fileService.deleteFolder(folder.idRepertoire).subscribe({
        next: () => {
          this.clearFolderLoading(folder.idRepertoire);
          this.closeFolderIfOpen(folder.idRepertoire);
          this.folders.splice(folderIndex, 1);
          const allIndex = this.allFolders.findIndex(f => f.idRepertoire === folder.idRepertoire);
          if (allIndex !== -1) this.allFolders.splice(allIndex, 1);
          this.refreshColumns();
          this.snackbarService.info(`Dossier "${folder.nom}" supprimé`, 2000);
        },
        error: (e) => {
          this.clearFolderLoading(folder.idRepertoire);
          this.snackbarService.error(`Erreur lors de la suppression du dossier "${folder.nom}"`);
        }
      })
    }
  }

  // Actions sur les fichiers
  addFile(columnIndex: number) {
    const dialogRef = this.dialog.open(FileUploadComponent, {
      data: { idRepertoire: this.columns[columnIndex].currentFolderId || 0 },
      disableClose: true
    });

    dialogRef.componentInstance.sendedFile.pipe(
      takeUntil(dialogRef.afterClosed())
    ).subscribe((newFile: UploadedFile) => {
      this.files.push(newFile);
      this.allFiles.push(newFile);
      this.refreshColumns();
    });
  }

  modifyFile(file: UploadedFile) {
    const dialogRef = this.dialog.open(DocumentEditComponent, {
      data: { file: structuredClone(file) }, // copie profonde plus sûre que Object.assign
      maxWidth: '600px'
    });

    dialogRef.afterClosed()
      .pipe(filter(Boolean))
      .subscribe((res) => {
        const fileIndex = this.files.findIndex(f => f.id === file.id);
        if (fileIndex === -1) return;

        res.newFile
          ? this.replaceFile(file, fileIndex, res)
          : this.renameFile(file, fileIndex, res.file.name);
      });
  }


  private replaceFile(original: UploadedFile, index: number, res: any): void {
    this.setFileLoading(original.id);
    this.fileService.replaceFile(original.id, res.newFile, res.file.name).pipe(
      filter((response: any) => typeof response === 'object' && response.status !== 'progress'),
      tap((response: UploadedFile) => {
        this.updateFileCollections(original.id, index, response);
      })
    ).subscribe({
      next: () => {
        this.snackbarService.info(`Modification du fichier "${original.name}"`, 2000);
      },
      error: () => {
        this.clearFileLoading(original.id);
        this.snackbarService.error(`Erreur lors de la modification du fichier "${original.name}"`);
      }
    });
  }

  private renameFile(original: UploadedFile, index: number, newName: string): void {
    this.setFileLoading(original.id);
    this.fileService.updateFileName(original.id, newName)
      .subscribe({
        next: (updated: UploadedFile) => {
          this.updateFileCollections(original.id, index, updated, false);
          this.snackbarService.info(`Fichier "${original.name}" renommé en "${updated.name}"`, 2000);
        },
        error: () => {
          this.clearFileLoading(original.id);
          this.snackbarService.error(`Erreur lors du renommage du fichier "${original.name}"`);
        }
      });
  }

  // private updateConfig(file: UploadedFile) {
  //   let exist = this.config.fileEdit.find(f => f.id === file.id);
  //   if (exist) {
  //     exist.dateModification = file.dateModification;
  //   } else {
  //     this.config.fileEdit.push({ id: file.id, uiid: file.uuid, dateModification: file.dateModification });
  //   }
  // }

  private updateFileCollections(id: number, index: number, updatedFile: UploadedFile, resetPreview = true): void {
    this.clearFileLoading(id);
    this.files.splice(index, 1, updatedFile);

    const allIndex = this.allFiles.findIndex(f => f.id === id);
    if (allIndex !== -1) {
      this.allFiles.splice(allIndex, 1, updatedFile);
    }

    // Mettre à jour le previewColumn si le fichier modifié est celui affiché
    if (this.previewColumn?.file.id === id) {
      this.previewColumn = {
        ...this.previewColumn,
        file: updatedFile,
        fileLoaded: resetPreview ? false : this.previewColumn.fileLoaded
      };
    }

    this.refreshColumns();
  }

  deleteFile(file: UploadedFile) {
    const fileIndex = this.files.findIndex(f => f.id === file.id);
    if (fileIndex !== -1) {
      this.setFileLoading(file.id);
      this.fileService.delete(file).subscribe({
        next: () => {
          this.clearFileLoading(file.id);
          this.files.splice(fileIndex, 1);
          const allIndex = this.allFiles.findIndex(f => f.id === file.id);
          if (allIndex !== -1) this.allFiles.splice(allIndex, 1);

          if (this.previewColumn?.file.id === file.id) {
            this.previewColumn = null;
            this.selectedFileId = 0;
            // Retomber sur le contexte dossier parent selon la vue active
            if (this.selectedFolderIds.length > 0) {
              this.selectedType = 'folder';
              this.selectedColumnIndex = this.selectedFolderIds.length - 1;
            } else if (this.treeSelectedFolderId) {
              this.selectedType = 'folder';
            } else if (this.singleColumnFolderHistory.length > 0) {
              this.selectedType = 'folder';
            } else {
              this.selectedType = null;
              this.selectedColumnIndex = null;
            }
          }

          this.fileSelection.deselect(file);
          this.refreshColumns();
          this.snackbarService.info(`Fichier "${file.name}" supprimé`, 2000);
        },
        error: () => {
          this.clearFileLoading(file.id);
          this.snackbarService.error(`Erreur lors de la suppression du fichier "${file.name}"`);
        }
      });
    }
  }

  // Multi-sélection
  toggleMultiSelect(columnIndex: number) {
    if (this.multiSelectColumnIndex === columnIndex) {
      this.multiSelectColumnIndex = null;
      this.fileSelection.clear();
    } else {
      this.multiSelectColumnIndex = columnIndex;
      this.fileSelection.clear();
    }
  }

  toggleFileMultiSelect(event: Event, file: UploadedFile) {
    event.stopPropagation();
    this.fileSelection.toggle(file);
  }

  isAllSelectedInColumn(columnIndex: number): boolean {
    const columnFiles = this.columns[columnIndex].files;
    return columnFiles.length > 0 && columnFiles.every(f => this.fileSelection.isSelected(f));
  }

  isSomeSelectedInColumn(columnIndex: number): boolean {
    const columnFiles = this.columns[columnIndex].files;
    return columnFiles.some(f => this.fileSelection.isSelected(f)) && !this.isAllSelectedInColumn(columnIndex);
  }

  toggleAllInColumn(columnIndex: number) {
    const columnFiles = this.columns[columnIndex].files;
    if (this.isAllSelectedInColumn(columnIndex)) {
      columnFiles.forEach(f => this.fileSelection.deselect(f));
    } else {
      columnFiles.forEach(f => this.fileSelection.select(f));
    }
  }

  deleteSelectedFiles() {
    const selected = this.fileSelection.selected;
    if (selected.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Supprimer les fichiers',
        message: `Voulez-vous vraiment supprimer ${selected.length} fichier(s) ?`,
        confirmText: 'Supprimer',
        cancelText: 'Annuler'
      }
    });

    dialogRef.afterClosed().pipe(filter(Boolean)).subscribe(() => {
      const filesToDelete = [...selected];
      let deletedCount = 0;
      filesToDelete.forEach(file => this.setFileLoading(file.id));

      from(filesToDelete).pipe(
        concatMap(file => this.fileService.delete(file))
      ).subscribe({
        next: () => {
          const file = filesToDelete[deletedCount];
          this.clearFileLoading(file.id);
          const fileIndex = this.files.findIndex(f => f.id === file.id);
          if (fileIndex !== -1) this.files.splice(fileIndex, 1);
          const allIndex = this.allFiles.findIndex(f => f.id === file.id);
          if (allIndex !== -1) this.allFiles.splice(allIndex, 1);

          if (this.previewColumn?.file.id === file.id) {
            this.previewColumn = null;
          }

          this.fileSelection.deselect(file);
          deletedCount++;
          this.refreshColumns();
        },
        complete: () => {
          this.fileSelection.clear();
          this.multiSelectColumnIndex = null;
          this.treeMultiSelect = false;
          this.refreshColumns();
          this.snackbarService.info(`${deletedCount} fichier(s) supprimé(s)`, 2000);
        },
        error: () => {
          const remaining = filesToDelete.slice(deletedCount);
          remaining.forEach(file => this.clearFileLoading(file.id));
          this.refreshColumns();
          this.snackbarService.error('Erreur lors de la suppression des fichiers');
        }
      });
    });
  }

  // Fil d'Ariane
  get breadcrumbs(): { label: string, columnIndex: number }[] {
    const crumbs = [{ label: 'Racine', columnIndex: 0 }];
    for (let i = 0; i < this.selectedFolderIds.length; i++) {
      const folderId = this.selectedFolderIds[i];
      if (folderId != null) {
        crumbs.push({ label: this.getFolderName(folderId), columnIndex: i + 1 });
      }
    }
    return crumbs;
  }

  get normalizedBreadcrumbs(): { label: string, index: number }[] {
    return this.breadcrumbs.map(c => ({ label: c.label, index: c.columnIndex }));
  }

  navigateToBreadcrumb(columnIndex: number) {
    this.columns = this.columns.slice(0, columnIndex + 1);
    this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);
    this.selectedFolderIds = this.selectedFolderIds.slice(0, columnIndex);
    this.selectedFileId = 0;
    this.selectedType = columnIndex > 0 ? 'folder' : null;
    this.selectedColumnIndex = columnIndex > 0 ? columnIndex - 1 : null;
    this.previewColumn = null;
  }

  getTypeFromFileName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'pdf': 'application/pdf',
      'txt': 'text/plain',
      'ts': 'text/typescript',
      'js': 'text/javascript',
      'css': 'text/css',
      'html': 'text/html',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return typeMap[ext || ''] || 'application/octet-stream';
  }

  // Tri
  setSortColumn(columnIndex: number, sortBy: SortOption) {
    const column = this.columns[columnIndex];
    if (column.sortBy === sortBy) {
      column.sortOrder = column.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      column.sortBy = sortBy;
      column.sortOrder = 'asc';
    }
    this.applySortToColumn(columnIndex);
  }

  applySortToColumn(columnIndex: number) {
    const column = this.columns[columnIndex];
    const order = column.sortOrder === 'asc' ? 1 : -1;

    const items: ColumnItem[] = [
      ...column.folders.map(f => ({ type: 'folder' as const, folder: f })),
      ...column.files.map(f => ({ type: 'file' as const, file: f }))
    ];

    items.sort((a, b) => {
      const getName = (item: ColumnItem) => item.type === 'folder' ? item.folder.nom : item.file.name;

      switch (column.sortBy) {
        case 'name':
          return getName(a).localeCompare(getName(b)) * order;
        case 'date':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return (this.getEffectiveDate(a.file) - this.getEffectiveDate(b.file)) * order;
        case 'size':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return (this.parseSizeToKB(a.file.taille) - this.parseSizeToKB(b.file.taille)) * order;
        case 'type':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return a.file.type.localeCompare(b.file.type) * order;
        default:
          return 0;
      }
    });

    column.items = items;
  }

  parseSizeToKB(input: string): number {
    const normalized = input.trim().replace(',', '.');

    const regex = /^([\d.]+)\s*(octets?|o|ko|mo|go)$/i;
    const match = normalized.match(regex);

    if (!match) {
      throw new Error(`Format invalide : "${input}"`);
    }

    const value = parseFloat(match[1]);
    const unit = match[2].toLowerCase();

    switch (unit) {
      case 'o':
      case 'octet':
      case 'octets':
        return value / 1024;
      case 'ko':
        return value;
      case 'mo':
        return value * 1024;
      case 'go':
        return value * 1024 * 1024;
      default:
        throw new Error(`Unité non supportée : "${unit}"`);
    }
  }

  private getEffectiveDate(file: UploadedFile): number {
    const invalidDate = '0000-00-00T00:00:00.000';
    const date = file.dateModification && file.dateModification !== invalidDate
      ? file.dateModification
      : file.creationDate;
    return new Date(date).getTime();
  }

  // Loading items helpers
  isFileLoading(fileId: number): boolean {
    return this.loadingItems.has(`file-${fileId}`);
  }

  isFolderLoading(folderId: number): boolean {
    return this.loadingItems.has(`folder-${folderId}`);
  }

  private setFileLoading(fileId: number): void {
    this.loadingItems.add(`file-${fileId}`);
  }

  private setFolderLoading(folderId: number): void {
    this.loadingItems.add(`folder-${folderId}`);
  }

  private clearFileLoading(fileId: number): void {
    this.loadingItems.delete(`file-${fileId}`);
  }

  private clearFolderLoading(folderId: number): void {
    this.loadingItems.delete(`folder-${folderId}`);
  }


  // Dialog
  private currentDialogRef: any;

  openDialog(title: string, label: string, defaultValue: string, confirmText: string, callback: (value: string) => void) {
    this.dialogTitle = title;
    this.dialogLabel = label;
    this.dialogValue = defaultValue;
    this.dialogConfirmText = confirmText;
    this.dialogCallback = callback;

    this.currentDialogRef = this.dialog.open(this.inputDialogTemplate, {
      width: '400px'
    });
  }

  confirmDialog() {
    if (this.dialogValue?.trim() && this.dialogCallback) {
      this.dialogCallback(this.dialogValue.trim());
      this.currentDialogRef?.close();
    }
  }

  // Drag & Drop
  onDragStart(event: DragEvent, type: 'file' | 'folder', item: UploadedFile | Folder, columnIndex: number) {
    this.dragData = { type, item, sourceColumnIndex: columnIndex };
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');
    }
  }

  onDragEnd() {
    this.dragData = null;
    this.isDragOver = null;
  }

  onDragOver(event: DragEvent, columnIndex: number) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.isDragOver = columnIndex;
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = null;
  }

  onDrop(event: DragEvent, targetColumnIndex: number) {
    event.preventDefault();
    this.isDragOver = null;

    if (!this.dragData) return;

    const targetFolderId = this.columns[targetColumnIndex].currentFolderId;

    if (this.dragData.type === 'file') {
      const file = this.dragData.item as UploadedFile;
      if (file.idRepertoire === (targetFolderId ?? 0)) {
        this.snackbarService.info('Le fichier est déjà dans ce dossier', 2000);
        return;
      }

      const fileIndex = this.files.findIndex(f => f.id === file.id);
      if (fileIndex !== -1) {
        this.setFileLoading(file.id);
        this.fileService.updateFileRepertoire(file.id, (targetFolderId ?? 0)).subscribe({
          next: (fileUpdated: UploadedFile) => {
            this.clearFileLoading(file.id);
            this.files[fileIndex].idRepertoire = targetFolderId ?? 0;
            // Fermer la preview si le fichier déplacé est celui affiché
            if (this.previewColumn?.file.id === file.id) {
              this.previewColumn = null;
            }
            this.snackbarService.info(`"${file.name}" déplacé vers ${this.getFolderName(targetFolderId ?? 0)}`, 2000);
            this.refreshColumns();
          },
          error: () => {
            this.clearFileLoading(file.id);
            this.snackbarService.error(`Erreur lors du déplacement du fichier "${file.name}"`);
          }
        })
      }
    } else if (this.dragData.type === 'folder') {
      const folder = this.dragData.item as Folder;

      if (this.isDescendant(targetFolderId, folder.idRepertoire)) {
        this.snackbarService.error('Impossible de déplacer un dossier dans lui-même ou ses sous-dossiers');
        return;
      }

      if (folder.idRepertoireParent === targetFolderId) {
        this.snackbarService.info('Le dossier est déjà à cet emplacement', 2000);
        return;
      }

      const folderIndex = this.folders.findIndex(f => f.idRepertoire === folder.idRepertoire);
      if (folderIndex !== -1) {
        this.setFolderLoading(folder.idRepertoire);
        this.fileService.updateFolder(folder.idRepertoire, { nom: folder.nom, idRepertoireParent: targetFolderId }).subscribe({
          next: (_) => {
            this.clearFolderLoading(folder.idRepertoire);
            this.folders[folderIndex].idRepertoireParent = targetFolderId;
            // Fermer le dossier déplacé s'il était ouvert
            this.closeFolderIfOpen(folder.idRepertoire);
            this.refreshColumns();
            this.snackbarService.info(`"${folder.nom}" déplacé vers ${this.getFolderName(targetFolderId ?? 0)}`, 2000);
          },
          error: (e) => {
            this.clearFolderLoading(folder.idRepertoire);
            this.snackbarService.error(`Erreur lors du déplacement du dossier "${folder.nom}"`);
          }
        })
      }
    }

    this.dragData = null;
  }


  closeFolderIfOpen(folderId: number): void {
    // Trouver l'index de la colonne qui correspond au dossier déplacé
    const columnIndex = this.selectedFolderIds.findIndex(id => id === folderId);

    if (columnIndex !== -1) {
      // Le dossier était ouvert, on ferme toutes les colonnes à partir de celle-ci
      this.selectedFolderIds = this.selectedFolderIds.slice(0, columnIndex);
      this.columns = this.columns.slice(0, columnIndex + 1);
      this.columnWidths = this.columnWidths.slice(0, columnIndex + 1);

      // Réinitialiser la sélection
      this.selectedType = columnIndex > 0 ? 'folder' : null;
      this.previewColumn = null;
    }
  }

  isDescendant(potentialDescendantId: number | null, ancestorId: number): boolean {
    if (potentialDescendantId === 0) return false;
    if (potentialDescendantId === ancestorId) return true;

    const folder = this.folders.find(f => f.idRepertoire === potentialDescendantId);
    if (!folder || folder.idRepertoireParent === 0) return false;

    return this.isDescendant(folder.idRepertoireParent, ancestorId);
  }

  refreshColumns() {
    this.columns = this.columns.map((col, index) => ({
      ...col,
      folders: this.getFoldersByParent(col.currentFolderId),
      files: this.getFilesByFolder(col.currentFolderId)
    }));

    this.columns.forEach((col, index) => this.applySortToColumn(index));
    this.refreshSingleColumn();
  }

  // Single column view
  initializeSingleColumn() {
    const folderId = this.singleColumnFolderHistory.length > 0
      ? this.singleColumnFolderHistory[this.singleColumnFolderHistory.length - 1]
      : 0;
    this.singleColumn = {
      folders: this.getFoldersByParent(folderId),
      files: this.getFilesByFolder(folderId),
      items: [],
      currentFolderId: folderId,
      sortBy: 'name',
      sortOrder: 'asc'
    };
    this.applySortToSingleColumn();
  }

  applySortToSingleColumn() {
    if (!this.singleColumn) return;
    const column = this.singleColumn;
    const order = column.sortOrder === 'asc' ? 1 : -1;

    const items: ColumnItem[] = [
      ...column.folders.map(f => ({ type: 'folder' as const, folder: f })),
      ...column.files.map(f => ({ type: 'file' as const, file: f }))
    ];

    items.sort((a, b) => {
      const getName = (item: ColumnItem) => item.type === 'folder' ? item.folder.nom : item.file.name;
      switch (column.sortBy) {
        case 'name':
          return getName(a).localeCompare(getName(b)) * order;
        case 'date':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return (this.getEffectiveDate(a.file) - this.getEffectiveDate(b.file)) * order;
        case 'size':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return (this.parseSizeToKB(a.file.taille) - this.parseSizeToKB(b.file.taille)) * order;
        case 'type':
          if (a.type === 'folder' && b.type === 'folder') return a.folder.nom.localeCompare(b.folder.nom);
          if (a.type === 'folder') return -1;
          if (b.type === 'folder') return 1;
          return a.file.type.localeCompare(b.file.type) * order;
        default:
          return 0;
      }
    });

    column.items = items;
  }

  setSortSingleColumn(sortBy: SortOption) {
    if (!this.singleColumn) return;
    if (this.singleColumn.sortBy === sortBy) {
      this.singleColumn.sortOrder = this.singleColumn.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.singleColumn.sortBy = sortBy;
      this.singleColumn.sortOrder = 'asc';
    }
    this.applySortToSingleColumn();
  }

  handleSingleFolderClick(folder: Folder) {
    this.singleColumnFolderHistory.push(folder.idRepertoire);
    this.selectedType = 'folder';
    this.selectedFileId = 0;
    this.previewColumn = null;

    if (this.selectMode && this.selection) {
      this.selection.clear();
    }

    this.initializeSingleColumn();
  }

  handleSingleFileClick(file: UploadedFile) {
    const isAlreadySelected = this.selectedFileId === file.id && this.selectedType === 'file';

    if (isAlreadySelected) {
      this.selectedFileId = 0;
      this.selectedType = null;
      this.previewColumn = null;
      if (this.selectMode && this.selection?.isSelected(file)) {
        this.selection.deselect(file);
      }
      return;
    }

    if (this.selectMode && this.selection) {
      if (this.selectMode !== 'multi') {
        this.selection.clear();
      }
      this.selection.select(file);
    }

    this.selectedFileId = file.id;
    this.selectedType = 'file';

    this.previewColumn = {
      file: file,
      currentFolderId: this.singleColumn?.currentFolderId || 0,
      fileLoaded: false
    };
  }

  toggleSingleFileWithPreview(event: Event, file: UploadedFile) {
    event.stopPropagation();
    if (this.selection?.isSelected(file)) {
      this.selection.deselect(file);
      this.selectedFileId = 0;
      this.selectedType = null;
      this.previewColumn = null;
    } else {
      if (this.selection) {
        if (this.selectMode !== 'multi') {
          this.selection.clear();
        }
        this.selection.select(file);
      }
      this.selectedFileId = file.id;
      this.selectedType = 'file';
      this.previewColumn = {
        file: file,
        currentFolderId: this.singleColumn?.currentFolderId || 0,
        fileLoaded: false
      };
    }
  }

  singleColumnGoBack() {
    if (this.singleColumnFolderHistory.length === 0) return;
    this.singleColumnFolderHistory.pop();
    this.selectedFileId = 0;
    this.selectedType = this.singleColumnFolderHistory.length > 0 ? 'folder' : null;
    this.previewColumn = null;
    this.initializeSingleColumn();
  }

  get singleBreadcrumbs(): { label: string, index: number }[] {
    const crumbs = [{ label: 'Racine', index: -1 }];
    for (let i = 0; i < this.singleColumnFolderHistory.length; i++) {
      crumbs.push({ label: this.getFolderName(this.singleColumnFolderHistory[i]), index: i });
    }
    return crumbs;
  }

  navigateToSingleBreadcrumb(index: number) {
    // index = -1 -> racine
    this.singleColumnFolderHistory = this.singleColumnFolderHistory.slice(0, index + 1);
    this.selectedFileId = 0;
    this.selectedType = index >= 0 ? 'folder' : null;
    this.previewColumn = null;
    this.initializeSingleColumn();
  }

  getSingleCurrentFolderId(): number {
    return this.singleColumn?.currentFolderId ?? 0;
  }

  createFolderInSingle() {
    const parentId = this.getSingleCurrentFolderId();
    this.openDialog(
      'Nouveau dossier',
      'Nom du dossier',
      'Nouveau dossier',
      'Créer',
      (name: string) => {
        const tempId = -Date.now();
        const tempFolder: Folder = {
          idRepertoire: tempId,
          idRepertoireParent: parentId,
          nom: name
        };
        this.folders.push(tempFolder);
        this.refreshSingleColumn();
        this.setFolderLoading(tempId);

        this.fileService.createFolder({ idRepertoire: null, idRepertoireParent: parentId, nom: name }).subscribe({
          next: (folder: Folder) => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1, folder);
            this.allFolders.push(folder);
            this.refreshSingleColumn();
            this.snackbarService.info(`Dossier "${name}" créé`, 2000);
          },
          error: () => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1);
            this.refreshSingleColumn();
            this.snackbarService.error(`Erreur lors de la création du dossier "${name}"`);
          }
        });
      }
    );
  }

  addFileInSingle() {
    const folderId = this.getSingleCurrentFolderId();
    const dialogRef = this.dialog.open(FileUploadComponent, {
      data: { idRepertoire: folderId },
      disableClose: true
    });

    dialogRef.componentInstance.sendedFile.pipe(
      takeUntil(dialogRef.afterClosed())
    ).subscribe((newFile: UploadedFile) => {
      this.files.push(newFile);
      this.allFiles.push(newFile);
      this.refreshSingleColumn();
    });
  }

  // Multi-sélection single column
  singleMultiSelect = false;

  toggleSingleMultiSelect() {
    this.singleMultiSelect = !this.singleMultiSelect;
    this.fileSelection.clear();
  }

  isAllSelectedInSingle(): boolean {
    if (!this.singleColumn) return false;
    const files = this.singleColumn.files;
    return files.length > 0 && files.every(f => this.fileSelection.isSelected(f));
  }

  isSomeSelectedInSingle(): boolean {
    if (!this.singleColumn) return false;
    const files = this.singleColumn.files;
    return files.some(f => this.fileSelection.isSelected(f)) && !this.isAllSelectedInSingle();
  }

  toggleAllInSingle() {
    if (!this.singleColumn) return;
    const files = this.singleColumn.files;
    if (this.isAllSelectedInSingle()) {
      files.forEach(f => this.fileSelection.deselect(f));
    } else {
      files.forEach(f => this.fileSelection.select(f));
    }
  }

  refreshSingleColumn() {
    if (this.singleColumn) {
      this.singleColumn.folders = this.getFoldersByParent(this.singleColumn.currentFolderId);
      this.singleColumn.files = this.getFilesByFolder(this.singleColumn.currentFolderId);
      this.applySortToSingleColumn();
    }
    this.buildTreeData();
  }

  // View mode
  onViewModeChange(mode: 'columns' | 'tree' | 'single') {
    const previousMode = this.viewMode;
    this.viewMode = mode;

    // Réinitialiser les multi-sélections
    this.treeMultiSelect = false;
    this.singleMultiSelect = false;
    this.multiSelectColumnIndex = null;
    this.fileSelection.clear();

    // Réinitialiser l'état d'expansion lors du passage en mode arborescence
    // pour éviter que d'anciens dossiers ouverts dans d'autres modes soient ré-expansés
    if (mode === 'tree') {
      this.expandedFolderIds.clear();
    }

    if (this.previewColumn) {
      // Un fichier est sélectionné : synchroniser le fichier
      const file = this.previewColumn.file;

      if (mode === 'tree') {
        const ancestors = this.getFolderAncestors(file.idRepertoire);
        for (const ancestor of ancestors) {
          this.expandedFolderIds.add(ancestor.idRepertoire);
        }
        this.treeSelectedFolderId = file.idRepertoire;
        this.selectedType = 'file';
        this.selectedFileId = file.id;
        this.restoreTreeExpansion();
      } else if (mode === 'single') {
        const ancestors = this.getFolderAncestors(file.idRepertoire);
        this.singleColumnFolderHistory = ancestors.map(a => a.idRepertoire);
        this.selectedType = 'file';
        this.selectedFileId = file.id;
        this.initializeSingleColumn();
      } else {
        const ancestors = this.getFolderAncestors(file.idRepertoire);
        this.initializeColumns();
        for (let i = 0; i < ancestors.length; i++) {
          this.handleFolderClick(ancestors[i], i);
        }
        const lastColumnIndex = this.columns.length - 1;
        this.handleFileClick(file, lastColumnIndex);
      }
    } else if (this.selectedType === 'folder') {
      // Seulement un dossier est sélectionné : synchroniser le dossier depuis le mode précédent
      let selectedFolderId: number | null;
      if (previousMode === 'columns') {
        selectedFolderId = this.selectedFolderIds[this.selectedFolderIds.length - 1] ?? null;
      } else if (previousMode === 'tree') {
        selectedFolderId = this.treeSelectedFolderId;
      } else {
        selectedFolderId = this.singleColumnFolderHistory.length > 0
          ? this.singleColumnFolderHistory[this.singleColumnFolderHistory.length - 1]
          : null;
      }

      if (mode === 'tree') {
        if (selectedFolderId != null) {
          const ancestors = this.getFolderAncestors(selectedFolderId);
          for (const ancestor of ancestors) {
            this.expandedFolderIds.add(ancestor.idRepertoire);
          }
          this.expandedFolderIds.add(selectedFolderId);
          this.treeSelectedFolderId = selectedFolderId;
          this.selectedType = 'folder';
          this.restoreTreeExpansion();
        }
      } else if (mode === 'single') {
        if (selectedFolderId != null) {
          const ancestors = this.getFolderAncestors(selectedFolderId);
          this.singleColumnFolderHistory = ancestors.map(a => a.idRepertoire);
        } else {
          this.singleColumnFolderHistory = [];
        }
        this.initializeSingleColumn();
      } else {
        if (selectedFolderId != null) {
          const folder = this.folders.find(f => f.idRepertoire === selectedFolderId);
          if (folder) {
            const ancestors = this.getFolderAncestors(folder.idRepertoireParent);
            this.selectedFolderIds = [];
            this.selectedType = null;
            this.initializeColumns();
            for (let i = 0; i < ancestors.length; i++) {
              this.handleFolderClick(ancestors[i], i);
            }
            this.handleFolderClick(folder, ancestors.length);
          }
        }
      }
    } else {
      // Rien de sélectionné
      if (mode === 'single') {
        this.singleColumnFolderHistory = [];
        this.initializeSingleColumn();
      } else if (mode === 'columns') {
        this.selectedFolderIds = [];
        this.selectedColumnIndex = null;
        this.initializeColumns();
      }
    }
  }

  buildTreeData() {
    const buildNode = (parentId: number | null): FileTreeNode[] => {
      const folderNodes: FileTreeNode[] = this.getFoldersByParent(parentId)
        .sort((a, b) => a.nom.localeCompare(b.nom))
        .map(folder => ({
          name: folder.nom,
          type: 'folder' as const,
          folder,
          children: [
            ...buildNode(folder.idRepertoire),
            ...this.getFilesByFolder(folder.idRepertoire)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(file => ({
                name: file.name,
                type: 'file' as const,
                file
              }))
          ]
        }));

      return folderNodes;
    };

    // Racine : dossiers racine + fichiers racine
    const rootFolders = buildNode(0);
    const rootFiles: FileTreeNode[] = this.getFilesByFolder(0)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(file => ({
        name: file.name,
        type: 'file' as const,
        file
      }));

    this.treeData = [...rootFolders, ...rootFiles];
    this.restoreTreeExpansion();
  }

  private restoreTreeExpansion() {
    if (this.expandedFolderIds.size === 0) return;
    setTimeout(() => {
      if (!this.fileTree) return;
      const expandNodes = (nodes: FileTreeNode[]) => {
        for (const node of nodes) {
          if (node.type === 'folder' && node.folder && this.expandedFolderIds.has(node.folder.idRepertoire)) {
            this.fileTree.expand(node);
          }
          if (node.children?.length) {
            expandNodes(node.children);
          }
        }
      };
      expandNodes(this.treeData);
    });
  }

  collapseAllTree() {
    this.expandedFolderIds.clear();
    if (this.fileTree) {
      this.fileTree.collapseAll();
    }
  }

  expandAllTree() {
    if (this.fileTree) {
      this.fileTree.expandAll();
    }
    const addAllFolderIds = (nodes: FileTreeNode[]) => {
      for (const node of nodes) {
        if (node.type === 'folder' && node.folder) {
          this.expandedFolderIds.add(node.folder.idRepertoire);
        }
        if (node.children?.length) {
          addAllFolderIds(node.children);
        }
      }
    };
    addAllFolderIds(this.treeData);
  }

  isAllTreeExpanded(): boolean {
    if (this.allFolders.length === 0) return false;
    return this.allFolders.every(f => this.expandedFolderIds.has(f.idRepertoire));
  }

  toggleExpandAllTree() {
    if (this.isAllTreeExpanded()) {
      this.collapseAllTree();
    } else {
      this.expandAllTree();
    }
  }

  onTreeNodeToggle(node: FileTreeNode) {
    if (node.type === 'folder' && node.folder) {
      if (this.expandedFolderIds.has(node.folder.idRepertoire)) {
        this.expandedFolderIds.delete(node.folder.idRepertoire);
      } else {
        this.expandedFolderIds.add(node.folder.idRepertoire);
      }
    }
  }

  handleTreeFileClick(file: UploadedFile) {
    if (this.selectedFileId === file.id && this.selectedType === 'file') {
      this.selectedFileId = 0;
      this.selectedType = null;
      this.previewColumn = null;

      if (this.selectMode && this.selection?.isSelected(file)) {
        this.selection.deselect(file);
      }
      return;
    }

    if (this.selectMode && this.selection) {
      if (this.selectMode !== 'multi') {
        this.selection.clear();
      }
      this.selection.select(file);
    }

    this.selectedFileId = file.id;
    this.selectedType = 'file';
    this.treeSelectedFolderId = file.idRepertoire;

    this.previewColumn = {
      file,
      currentFolderId: file.idRepertoire || 0,
      fileLoaded: false
    };
  }

  handleTreeFolderClick(folder: Folder, node?: FileTreeNode) {
    if (this.treeSelectedFolderId === folder.idRepertoire && this.selectedType === 'folder') {
      this.treeSelectedFolderId = null;
      this.selectedType = null;
      if (node && this.fileTree) {
        this.fileTree.collapse(node);
        this.expandedFolderIds.delete(folder.idRepertoire);
      }
      return;
    }

    this.treeSelectedFolderId = folder.idRepertoire;
    this.selectedType = 'folder';
    this.selectedFileId = 0;
    this.previewColumn = null;

    if (this.selectMode && this.selection) {
      this.selection.clear();
    }

    if (node && this.fileTree) {
      this.fileTree.expand(node);
      this.expandedFolderIds.add(folder.idRepertoire);
    }
  }

  getTreeCurrentFolderId(): number {
    return this.treeSelectedFolderId ?? 0;
  }

  createFolderInTree() {
    const parentId = this.getTreeCurrentFolderId();
    this.openDialog(
      'Nouveau dossier',
      'Nom du dossier',
      'Nouveau dossier',
      'Créer',
      (name: string) => {
        const tempId = -Date.now();
        const tempFolder: Folder = {
          idRepertoire: tempId,
          idRepertoireParent: parentId,
          nom: name
        };
        this.folders.push(tempFolder);
        this.refreshColumns();
        this.setFolderLoading(tempId);

        this.fileService.createFolder({ idRepertoire: null, idRepertoireParent: parentId, nom: name }).subscribe({
          next: (folder: Folder) => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1, folder);
            this.allFolders.push(folder);
            this.refreshColumns();
            this.snackbarService.info(`Dossier "${name}" créé`, 2000);
          },
          error: () => {
            this.clearFolderLoading(tempId);
            const tempIndex = this.folders.findIndex(f => f.idRepertoire === tempId);
            if (tempIndex !== -1) this.folders.splice(tempIndex, 1);
            this.refreshColumns();
            this.snackbarService.error(`Erreur lors de la création du dossier "${name}"`);
          }
        });
      }
    );
  }

  addFileInTree() {
    const folderId = this.getTreeCurrentFolderId();
    const dialogRef = this.dialog.open(FileUploadComponent, {
      data: { idRepertoire: folderId },
      disableClose: true
    });

    dialogRef.componentInstance.sendedFile.pipe(
      takeUntil(dialogRef.afterClosed())
    ).subscribe((newFile: UploadedFile) => {
      this.files.push(newFile);
      this.allFiles.push(newFile);
      this.refreshColumns();
    });
  }

  // Multi-sélection arborescence
  toggleTreeMultiSelect() {
    this.treeMultiSelect = !this.treeMultiSelect;
    this.fileSelection.clear();
  }

  toggleTreeFileMultiSelect(event: Event, file: UploadedFile) {
    event.stopPropagation();
    this.fileSelection.toggle(file);
  }

  isAllSelectedInTree(): boolean {
    return this.files.length > 0 && this.files.every(f => this.fileSelection.isSelected(f));
  }

  isSomeSelectedInTree(): boolean {
    return this.fileSelection.selected.length > 0 && !this.isAllSelectedInTree();
  }

  toggleAllInTree() {
    if (this.isAllSelectedInTree()) {
      this.fileSelection.clear();
    } else {
      this.files.forEach(f => this.fileSelection.select(f));
    }
  }

  isAllSelectedInFolder(folderId: number): boolean {
    const folderFiles = this.getFilesByFolder(folderId);
    return folderFiles.length > 0 && folderFiles.every(f => this.fileSelection.isSelected(f));
  }

  isSomeSelectedInFolder(folderId: number): boolean {
    const folderFiles = this.getFilesByFolder(folderId);
    return folderFiles.some(f => this.fileSelection.isSelected(f)) && !this.isAllSelectedInFolder(folderId);
  }

  toggleAllInFolder(event: Event, folderId: number) {
    event.stopPropagation();
    const folderFiles = this.getFilesByFolder(folderId);
    if (this.isAllSelectedInFolder(folderId)) {
      folderFiles.forEach(f => this.fileSelection.deselect(f));
    } else {
      folderFiles.forEach(f => this.fileSelection.select(f));
    }
  }

  // Recherche globale
  onSearchChange() {
    if (!this.searchQuery.trim()) {
      this.searchResults = null;
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.searchResults = {
      folders: this.allFolders.filter(f => f.nom.toLowerCase().includes(query)),
      files: this.allFiles.filter(f => f.name.toLowerCase().includes(query))
    };
  }

  clearSearch() {
    this.searchQuery = '';
    this.searchResults = null;
  }

  getFolderPath(folderId: number): string {
    if (!folderId || folderId === 0) return 'Racine';
    const ancestors = this.getFolderAncestors(folderId);
    if (ancestors.length === 0) return 'Racine';
    return 'Racine / ' + ancestors.map(f => f.nom).join(' / ');
  }

  private getFolderAncestors(folderId: number): Folder[] {
    if (!folderId || folderId === 0) return [];
    const path: Folder[] = [];
    let currentId = folderId;
    while (currentId && currentId !== 0) {
      const folder = this.allFolders.find(f => f.idRepertoire === currentId);
      if (!folder) break;
      path.unshift(folder);
      currentId = folder.idRepertoireParent;
    }
    return path;
  }

  navigateToFile(file: UploadedFile) {
    this.searchQuery = '';
    this.searchResults = null;

    if (this.viewMode === 'tree') {
      this.collapseAllTree();
      const ancestors = this.getFolderAncestors(file.idRepertoire);
      for (const ancestor of ancestors) {
        this.expandedFolderIds.add(ancestor.idRepertoire);
      }
      this.restoreTreeExpansion();
      this.handleTreeFileClick(file);
      return;
    }

    if (this.viewMode === 'single') {
      const ancestors = this.getFolderAncestors(file.idRepertoire);
      this.singleColumnFolderHistory = ancestors.map(a => a.idRepertoire);
      this.initializeSingleColumn();
      this.handleSingleFileClick(file);
      return;
    }

    const ancestors = this.getFolderAncestors(file.idRepertoire);
    this.initializeColumns();

    for (let i = 0; i < ancestors.length; i++) {
      this.handleFolderClick(ancestors[i], i);
    }

    const lastColumnIndex = this.columns.length - 1;
    this.handleFileClick(file, lastColumnIndex);
  }

  navigateToFolder(folder: Folder) {
    this.searchQuery = '';
    this.searchResults = null;

    if (this.viewMode === 'tree') {
      this.collapseAllTree();
      const ancestors = this.getFolderAncestors(folder.idRepertoireParent);
      for (const ancestor of ancestors) {
        this.expandedFolderIds.add(ancestor.idRepertoire);
      }
      this.expandedFolderIds.add(folder.idRepertoire);
      this.restoreTreeExpansion();
      this.handleTreeFolderClick(folder);
      return;
    }

    if (this.viewMode === 'single') {
      const ancestors = this.getFolderAncestors(folder.idRepertoireParent);
      this.singleColumnFolderHistory = [...ancestors.map(a => a.idRepertoire), folder.idRepertoire];
      this.initializeSingleColumn();
      return;
    }

    const ancestors = this.getFolderAncestors(folder.idRepertoireParent);
    this.initializeColumns();

    for (let i = 0; i < ancestors.length; i++) {
      this.handleFolderClick(ancestors[i], i);
    }

    this.handleFolderClick(folder, ancestors.length);
  }

  getFileIcon(type: string): string {
    if (type.startsWith('image/')) return 'image';
    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('word') || type.includes('document')) return 'description';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'table_chart';
    if (type.includes('text')) return 'text_snippet';
    if (type.includes('typescript') || type.includes('javascript')) return 'code';
    if (type.includes('css')) return 'css';
    return 'insert_drive_file';
  }

  isImage(type: string): boolean {
    return type.startsWith('image/');
  }

  isPDF(type: string): boolean {
    return type.includes('pdf');
  }

  openFile(file: UploadedFile) {
    window.open(environment.apiUrl + 'files/' + file.id, '_blank');
  }

  startResize(event: MouseEvent, columnIndex: number) {
    event.preventDefault();
    this.resizingColumnIndex = columnIndex;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.resizingColumnIndex !== null && this.containerRef) {
      const container = this.containerRef.nativeElement;
      const rect = container.getBoundingClientRect();
      let totalWidth = 0;

      for (let i = 0; i < this.resizingColumnIndex; i++) {
        totalWidth += this.columnWidths[i];
      }

      const scrollLeft = this.containerRef.nativeElement.scrollLeft;
      const newWidth = Math.max(200, Math.min(500, event.clientX - rect.left + scrollLeft - totalWidth));
      this.columnWidths = [...this.columnWidths];
      this.columnWidths[this.resizingColumnIndex] = newWidth;
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.resizingColumnIndex = null;
  }
}
